import React from "react";
import { ContainerData, DispatchData, getContainer, stockDispatch } from "../../api/stock";
import useLocale from "../../util/i18n";
import { useCrumbs } from "../../component/breadcrumbs";
import { Helmet } from "react-helmet-async";
import Pager from "../../component/pager";
import { useSearchParams } from "react-router-dom";
import { MDBBtn, MDBCard, MDBCardHeader, MDBCardTitle, MDBModal, MDBModalContent, MDBModalDialog, MDBTable, MDBTableBody } from "mdb-react-ui-kit";
import BigNumber from "bignumber.js";
import { faAngleDown, faFileArrowDown, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarXmark } from "@fortawesome/free-regular-svg-icons";
import TagInfo from "../../util/tag";
import ExcelJS from "exceljs";
import { format } from "date-fns";

export default function Dispatch() {
    const {0: containers, 1: setContainers} = React.useState<Array<ContainerData>>();
    const {0: total, 1: setTotal} = React.useState(0);
    const {0: search, 1: setSearch} = useSearchParams();
    const {0: __} = useLocale();
    const {0: setCrumbs} = useCrumbs();
    const table = React.useRef<ExportRef>(null);
    let doExport = React.useRef<ExportRef>((container, items) => {
        table.current?.(container, items);
    });
    React.useEffect(() => {
        getContainer(search).then(response => {
            if (typeof response !== 'string') {
                setContainers(response.data);
                setTotal(response.total);
            }
        });
    }, [search]);
    React.useEffect(() => {
        setCrumbs(['Stock Out History']);
    }, []);
    return (
        <>
            <Helmet prioritizeSeoTags>
                <title>{__('Stock Out History')}</title>
            </Helmet>
            <div className="inventories">
                {typeof containers === 'undefined' ? (
                    <div className="loading"><FontAwesomeIcon icon={faSpinner} spin /></div>
                ) : (containers.length ? containers.map(item => (
                    <Item key={item.code} container={item} export={doExport.current} />
                )) : (
                    <div className="empty">
                        <FontAwesomeIcon icon={faCalendarXmark} />
                        <span>{__('No Data to Display')}</span>
                    </div>
                ))}
            </div>
            {containers?.length ? (<Pager total={total} search={search} onPage={setSearch} />) : null}
            <Export ref={table} />
        </>
    );
}

function Item(props: { container: ContainerData, export: ExportRef }) {
    const {0: open, 1: setOpen} = React.useState(false);
    const {0: items, 1: setItems} = React.useState<Array<DispatchData>>();
    const {0: __, 1: lang} = useLocale();
    let doExport = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        (e.target as HTMLButtonElement).disabled = true;
        load().then((items) => {
            props.export(props.container.code, items);
            (e.target as HTMLButtonElement).disabled = false;
        }, () => {
            (e.target as HTMLButtonElement).disabled = false
        });
    }, [props.container, items]);
    let load = React.useCallback(() => {
        if (typeof items !== 'undefined') {
            return Promise.resolve([]);
        }
        return new Promise<Array<DispatchData>>(resolve => {
            stockDispatch(props.container.code).then(response => {
                if (typeof response !== 'string') {
                    let items: Record<string, DispatchData> = {}, ps: Array<Promise<void>> = [];
                    for (let item of response) {
                        ps.push(new Promise(resolve => {
                            let tmp = { ...item };
                            TagInfo.fromEpc(tmp.sku, tag => {
                                tmp.sku = tag.sku;
                                if (typeof items[tmp.sku] === 'undefined') {
                                    items[tmp.sku] = { ...tmp, epcs: [tag.code] };
                                } else {
                                    items[tmp.sku].qty = (new BigNumber(tmp.qty)).plus(items[tmp.sku].qty).toNumber();
                                    items[tmp.sku].epcs.push(tag.code);
                                }
                                resolve();
                            });
                        }));
                    }
                    Promise.all(ps).then(() => {
                        let array = Object.values(items);
                        setItems(array);
                        resolve(array);
                    });
                } else {
                    setItems([]);
                    resolve([]);
                }
                window.setTimeout(() => ref.current && (ref.current.style.height = 'auto'), 100);
            }, () => {
                setItems([]);
                resolve([]);
            });
        });
    }, [props.container, items, setItems]);
    const ref = React.useRef<HTMLUListElement>(null);
    React.useEffect(() => {
        if (open) {
            load();
        }
    }, [open, load]);
    return (
        <MDBCard className="inventory">
            <MDBCardHeader>
                <MDBCardTitle>{__('Container #%s').replace('%s', props.container.code)} {props.container.order_id && __('Order') + ': ' + props.container.order_id}</MDBCardTitle>
                <div className="d-flex align-items-center">
                    <span className="d-none d-md-block">{Intl.DateTimeFormat(lang, { dateStyle: 'medium', timeStyle: 'short', hour12: false }).format(props.container.created_at * 1000)}</span>
                    <MDBBtn className="export" type="button" color="link" onClick={doExport}>
                        <FontAwesomeIcon icon={faFileArrowDown} />
                        <FontAwesomeIcon icon={faSpinner} spin />
                    </MDBBtn>
                    <MDBBtn className="toggler" type="button" color="link" onClick={setOpen.bind(null, !open)}>
                        <FontAwesomeIcon icon={faAngleDown} style={{ transform: 'rotate(' + (open ? '180deg' : 0) + ')' }} />
                    </MDBBtn>
                </div>
            </MDBCardHeader>
            <dl className="card-body" ref={ref} style={open ? { height: ((ref.current?.scrollHeight || 0) + 20) + 'px', paddingTop: '10px', paddingBottom: '10px' } : { height: '0px' }}>
                {typeof items === 'undefined' ? (
                    <dt className="loading"><FontAwesomeIcon icon={faSpinner} spin /></dt>
                ) : (items.length ? (
                    <>
                        <dt>SKU</dt>
                        <dt>{__('Quantity')}</dt>
                        {items.map(item => (
                            <React.Fragment key={item.sku}>
                                <dt title={item.epcs.join(',')}>{item.sku}</dt>
                                <dd>{parseFloat(item.qty.toString())}</dd>
                            </React.Fragment>
                        ))}
                    </>
                ) : (
                    <dt className="empty">
                        <FontAwesomeIcon icon={faCalendarXmark} />
                        <span>{__('No Data to Display')}</span>
                    </dt>
                ))}
            </dl>
        </MDBCard>
    );
}

type ExportRef = (container: string, items: Array<DispatchData>) => void;

const Export = React.forwardRef<ExportRef>(function (_, ref) {
    let { 0: container, 1: setContainer } = React.useState('');
    let { 0: items, 1: setItems } = React.useState<Array<DispatchData>>([]);
    let { 0: data, 1: setData } = React.useState<Record<string, string>>({});
    let { 0: __ } = useLocale();
    let onChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setData(old => ({ ...old, [e.target.name]: e.target.value }));
    }, [setData]);
    let doExport = React.useCallback((e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e.stopPropagation();
        e.preventDefault();
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet();
        sheet.getCell(1, 1).value = __('Container');
        sheet.getCell(1, 2).value = container;
        sheet.getCell(2, 1).value = __('Customer Information');
        sheet.getCell(2, 2).value = data.customer ?? '';
        sheet.getCell(3, 1).value = __('Purchase Order');
        sheet.getCell(3, 2).value = data.po ?? '';
        sheet.getCell(4, 1).value = __('Date');
        sheet.getCell(4, 2).value = data.date ?? format(new Date(), 'yyyy-MM-dd');
        sheet.getCell(5, 1).value = __('Destination');
        sheet.getCell(5, 2).value = data.dest ?? '';
        sheet.getCell(6, 1).value = 'SKU';
        sheet.getCell(6, 2).value = __('Quantity');
        sheet.getCell(1, 1).style.font = sheet.getCell(2, 1).style.font = sheet.getCell(3, 1).style.font = sheet.getCell(4, 1).style.font =
            sheet.getCell(5, 1).style.font = sheet.getCell(6, 1).style.font = sheet.getCell(6, 2).style.font = { bold: true };
        let row = 7;
        for (const item of items) {
            sheet.getCell(row, 1).value = item.sku;
            sheet.getCell(row, 2).value = parseFloat(item.qty.toString());
        }
        workbook.xlsx.writeBuffer().then(buffer => {
            let a = document.createElement('a');
            a.download = 'export.xlsx';
            a.href = URL.createObjectURL(new Blob([buffer]));
            document.body.append(a);
            a.click();
            URL.revokeObjectURL(a.href);
            a.remove();
        });
    }, [items, container, data]);
    let dismiss = React.useCallback(() => {
        setContainer('');
        setItems([]);
        setData({});
    }, [setContainer, setItems, setData]);
    React.useImperativeHandle(ref, () => (container, items) => {
        setContainer(container);
        setItems(items);
    }, [setContainer]);
    return (
        <MDBModal open={Boolean(container)} onClose={dismiss} tabIndex='-1'>
            <MDBModalDialog>
                <MDBModalContent tag="form" onSubmit={doExport}>
                    <MDBTable>
                        <MDBTableBody>
                            <tr>
                                <th>{__('Container')}</th>
                                <td>{container}</td>
                            </tr>
                            <tr>
                                <th>{__('Customer Information')}</th>
                                <td><input name="customer" value={data.customer ?? ''} onChange={onChange} /></td>
                            </tr>
                            <tr>
                                <th>{__('Purchase Order')}</th>
                                <td><input name="po" value={data.po ?? ''} onChange={onChange} /></td>
                            </tr>
                            <tr>
                                <th>{__('Date')}</th>
                                <td><input type="date" name="date" value={data.date ?? format(new Date(), 'yyyy-MM-dd')} onChange={onChange} /></td>
                            </tr>
                            <tr>
                                <th>{__('Destination')}</th>
                                <td><input name="dest" value={data.dest ?? ''} onChange={onChange} /></td>
                            </tr>
                            <tr><th>SKU</th><th>{__('Quantity')}</th></tr>
                            {items.map(item => (
                                <tr key={item.sku}>
                                    <th>{item.sku}</th>
                                    <td>{parseFloat(item.qty.toString())}</td>
                                </tr>
                            ))}
                        </MDBTableBody>
                    </MDBTable>
                    <MDBBtn color="primary" type="submit">{__('Export')}</MDBBtn>
                </MDBModalContent>
            </MDBModalDialog>
        </MDBModal>
    );
});
